'use client';
import React, {
  ChangeEventHandler,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import NextImage from 'next/image';

import { TypeMarketingHeroFields } from '@/core/generated-types';
import {
  Button,
  Image,
  InputText,
  RichText,
  UniversalCta,
  UniversalLink,
} from '@/components';
import { BUTTON_STYLES } from '@/components/ui/Buttons/utils';
import checkImgSrc from '@/public/assets/icons/check.svg';
import { TypeUserRecord } from '@/lib/api/accountManager/types';
import { getAuthTokenClient } from '@/utils/helpers';
import { Document } from '@contentful/rich-text-types';
import { track } from '@/utils/rudderstack';
import { REFERRAL_EVENT_LABELS } from '@/core/constants';

export interface MarketingHeroProps extends TypeMarketingHeroFields {
  user?: TypeUserRecord;
}

async function fetchShareCode(user?: TypeUserRecord, email?: string) {
  const response = await fetch('/api/v1/referrals/', {
    method: 'POST',
    body: JSON.stringify({
      advocate_email: user?.email,
      redeemer_emails: email,
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: await getAuthTokenClient(),
    },
  });

  if (!response.ok) {
    throw (await response.json()).error.fallback;
  }

  return await response.json();
}

const STATIC_EMAIL = 'start-referral@lovevery.com';

const MarketingHero: FC<MarketingHeroProps> = ({
  cta,
  image,
  headerText,
  subHeaderText,
  description,
  secondaryCta,
  backgroundColor,
  emailInputLabel = "Your Friend's Email",
  emailInputPlaceholder = 'Email',
  emailButtonText = 'Send emails',
  dividerText = 'OR',
  copyButtonText = 'Copy',
  shareLinkPrompt = 'Share your link:',
  sendEmailErrorMessage = 'Please provide a valid email address.',
  successButtonText = 'Success!',
  user,
}) => {
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [advocateUrl, setAdvocateUrl] = useState('');
  const [isUserAllowed, setIsUserAllowed] = useState(false);

  // Grab initial share code
  useEffect(() => {
    if (!user) return;

    (async () => {
      try {
        const { link_url: advocateUrl } = await fetchShareCode(
          user,
          STATIC_EMAIL,
        );
        setIsUserAllowed(true);
        setAdvocateUrl(advocateUrl);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [user]);

  const handleEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      setEmail(event.target.value.trim());
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const hasTld = email.includes('.');

      if (!email || !emailRef.current?.validity.valid || !hasTld) {
        setErrorMessage(sendEmailErrorMessage);
        return;
      }

      setErrorMessage('');
      setIsLoading(true);

      try {
        // When the user is blocked or has their referral code disabled,
        // the UI will not explicitly show any error messages but will
        // fail silently without warning the user.
        if (isUserAllowed) {
          await fetch('/api/v1/referrals/', {
            method: 'POST',
            body: JSON.stringify({
              advocate_email: user?.email,
              redeemer_emails: email,
            }),
            headers: {
              'Content-Type': 'application/json',
              Authorization: await getAuthTokenClient(),
            },
          });
          track(REFERRAL_EVENT_LABELS.REFERRAL_GENERATED, {
            redeemerEmail: email,
            advocateEmail: user?.email,
          });
        }
        setIsEmailSent(true);
        setEmail('');
        setTimeout(() => {
          setIsEmailSent(false);
        }, 2000);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [email, sendEmailErrorMessage, isUserAllowed, user?.email],
  );

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(advocateUrl);
    setIsCopied(true);
    track(REFERRAL_EVENT_LABELS.REFERRAL_LINK_COPIED, {
      advocateEmail: user?.email,
    });
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  }, [advocateUrl, user?.email]);

  return (
    <div
      className={`flex flex-col lg:flex-row bg-${backgroundColor?.fields?.color}`}
      data-testid="marketing-hero"
    >
      {/* Mobile media */}
      <div className="lg:hidden py-4 text-center px-8 lg:px-0">
        <div className="mb-2 text-d-h2 font-bold lg:text-d-h1">
          {headerText}
        </div>
        <div className="mb-2 text-d-h4 font-bold lg:text-d-h2">
          {subHeaderText}
        </div>
      </div>

      {/* Desktop media */}
      <div className="lg:w-1/2 flex-none lg:h-auto h-[240px]">
        {image && (
          <Image
            imageData={image}
            alt={image.fields?.altText}
            className="object-cover w-full h-full"
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-none px-8 md:px-16 lg:px-24 lg:w-1/2 w-full lg:py-12 py-6 flex items-center">
        <div className="w-full">
          {/* Text content */}
          <div className="lg:max-w-xl">
            <div className="hidden lg:block lg:px-0">
              <div className="mb-1 text-d-h2 font-bold lg:text-d-h1">
                {headerText}
              </div>
              <div className="mb-4 text-d-h3 font-bold lg:text-d-h2">
                {subHeaderText}
              </div>
            </div>
            <div className="mx-auto md:text-lg text-base mb-6">
              <RichText content={description as Document} />
            </div>
          </div>

          {/* Actions */}
          <div className="lg:max-w-sm">
            {user?.email ? (
              <>
                {/* Email input */}
                <form onSubmit={handleSubmit}>
                  <InputText
                    type="email"
                    ref={emailRef}
                    placeholder={emailInputPlaceholder}
                    onChange={handleEmailChange}
                    value={email}
                    label={emailInputLabel}
                    error={errorMessage}
                  />
                  <Button
                    type="submit"
                    classes="btn-responsive mt-6 w-full"
                    ctaText={isEmailSent ? successButtonText : emailButtonText}
                    disabled={isLoading}
                    includeSpinner={isLoading}
                  />
                </form>

                {isUserAllowed && (
                  <>
                    <div className="flex items-center pt-8 pb-4">
                      <div className="border-b flex-auto border-grey-midtone" />
                      <div className="flex-none text-grey-midtone px-8">
                        {dividerText}
                      </div>
                      <div className="border-b flex-auto border-grey-midtone" />
                    </div>

                    {/* Share link */}
                    <div>
                      <div className="mb-2">{shareLinkPrompt}</div>
                      <div className="bg-white text-lg flex items-stretch">
                        <div className="-mr-1 border-l border-t border-b border-grey-midtone truncate leading-[48px] px-4 flex-auto">
                          {advocateUrl}
                        </div>
                        <Button
                          classes="btn-responsive flex-none w-auto h-auto relative"
                          ctaText={
                            (
                              <>
                                <span className={isCopied ? 'opacity-0' : ''}>
                                  {copyButtonText}
                                </span>
                                {isCopied && (
                                  <span className="absolute z-10 w-full h-full flex items-center justify-center">
                                    <NextImage
                                      src={checkImgSrc}
                                      alt="Check icon"
                                      height={24}
                                      width={24}
                                      className="h-[24px] w-[24px]"
                                    />
                                  </span>
                                )}
                              </>
                            ) as any
                          }
                          onClick={handleCopy}
                          style={BUTTON_STYLES.secondary}
                        />
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                {cta && (
                  <div className="lg:mt-8 mt-6 flex justify-center lg:justify-start">
                    <UniversalCta
                      link={cta}
                      defaultButtonStyle="Primary"
                      className="btn-responsive"
                      testId="primary-cta"
                    />
                  </div>
                )}
              </>
            )}

            {/* Secondary link */}
            {secondaryCta && (
              <div className="mt-6 text-sm md:text-base flex justify-center lg:justify-start">
                <UniversalLink
                  link={secondaryCta}
                  classes="underline"
                  data-testid="secondary-cta"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(MarketingHero);
